import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Badge, Input, Modal, Tabs } from '../../../components/ui';
import { Wallet, Calendar, RefreshCw, Plus, ArrowDownCircle, ArrowUpCircle, DollarSign, Store, ChevronDown, ChevronRight, FileText, Clock, Archive } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';
import { fechaLocalYMD } from '../../../utils/date';

// Tipos
interface SaldoLocal {
  local_id: string;
  local_nombre: string;
  saldo_actual: string;
  updated_at: string | null;
  ultimo_movimiento: {
    tipo: string;
    fecha: string;
    monto: string;
  } | null;
}

interface PreviewLiquidacion {
  local_id: string;
  local_nombre: string;
  fecha: string;
  total_vendido: string;
  total_premios: string;
  total_comision: string;
  neto_dia: string;
  saldo_actual: string;
  sorteos: { sorteo: string; vendido: string; premios: string; comision: string }[];
}

interface PreviewCierre {
  local_id: string;
  local_nombre: string;
  fecha_hasta: string;
  saldo_actual: string;
  movimientos_a_eliminar: number;
  movimientos_posteriores: number;
  ya_consolidado: boolean;
}

interface Movimiento {
  id: string;
  local_id: string;
  local_nombre: string;
  tipo: string;
  monto: string;
  referencia: string;
  fecha: string;
  saldo_resultante: string;
  detalle: {
    vendido?: string;
    premios?: string;
    comision?: string;
    sorteos?: { sorteo: string; vendido: string; premios: string; comision: string }[];
    movimientos_consolidados?: number;
    fecha_desde?: string;
    fecha_hasta?: string;
  } | null;
  created_by: string;
  created_at: string;
}

const TIPO_LABELS: Record<string, string> = {
  LIQUIDACION_DIA: 'Liquidación',
  CIERRE_CONSOLIDADO: 'Cierre',
  ABONO: 'Abono',
  PAGO: 'Pago',
};

const TIPO_BADGE: Record<string, 'primary' | 'success' | 'warning' | 'danger' | 'neutral'> = {
  LIQUIDACION_DIA: 'primary',
  CIERRE_CONSOLIDADO: 'neutral',
  ABONO: 'success',
  PAGO: 'warning',
};

const formatMonto = (val: string) => {
  const n = Number(val);
  if (isNaN(n)) return val;
  return n.toLocaleString('es-CR', { minimumFractionDigits: 0 });
};

// ========================
// TAB 1: SALDOS
// ========================
const SaldosTab: React.FC = () => {
  const [saldos, setSaldos] = useState<SaldoLocal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal abono/pago
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTipo, setModalTipo] = useState<'ABONO' | 'PAGO'>('ABONO');
  const [localSeleccionado, setLocalSeleccionado] = useState<SaldoLocal | null>(null);
  const [monto, setMonto] = useState('');
  const [referencia, setReferencia] = useState('');
  const [guardando, setGuardando] = useState(false);

  // Modal historial
  const [historialOpen, setHistorialOpen] = useState(false);
  const [historialLocal, setHistorialLocal] = useState<SaldoLocal | null>(null);
  const [movimientos, setMovimientos] = useState<Movimiento[]>([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);

  const cargarSaldos = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAPI<SaldoLocal[]>('/saldos');
      setSaldos(data);
    } catch (e: any) {
      setError(e.message || 'Error al cargar saldos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarSaldos();
  }, [cargarSaldos]);

  const abrirModal = (local: SaldoLocal, tipo: 'ABONO' | 'PAGO') => {
    setLocalSeleccionado(local);
    setModalTipo(tipo);
    setMonto('');
    setReferencia('');
    setError('');
    setModalOpen(true);
  };

  const guardar = async () => {
    if (!localSeleccionado || !monto) return;
    setGuardando(true);
    setError('');
    try {
      const endpoint = modalTipo === 'ABONO' ? '/saldos/abono' : '/saldos/pago';
      await fetchAPI<any>(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          local_id: localSeleccionado.local_id,
          monto: parseFloat(monto),
          referencia: referencia || undefined,
        }),
      });
      setModalOpen(false);
      cargarSaldos();
    } catch (e: any) {
      setError(e.message || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const abrirHistorial = async (local: SaldoLocal) => {
    setHistorialLocal(local);
    setHistorialOpen(true);
    setLoadingHistorial(true);
    try {
      const data = await fetchAPI<Movimiento[]>(`/saldos/movimientos?local_id=${local.local_id}&limit=30`);
      setMovimientos(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoadingHistorial(false);
    }
  };

  // Stats
  const totalDeben = saldos.reduce((acc, s) => {
    const n = Number(s.saldo_actual);
    return n > 0 ? acc + n : acc;
  }, 0);
  const totalDebemos = saldos.reduce((acc, s) => {
    const n = Number(s.saldo_actual);
    return n < 0 ? acc + Math.abs(n) : acc;
  }, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Saldos por Local</h1>
          <p className="text-gray-500">Estado financiero de cada local</p>
        </div>
        <button
          onClick={cargarSaldos}
          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
          title="Refrescar"
        >
          <RefreshCw size={20} />
        </button>
      </div>

      {/* Error */}
      {error && !modalOpen && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <ArrowUpCircle size={20} className="text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Nos Deben</p>
              <p className="text-2xl font-bold text-red-600">{formatMonto(String(totalDeben))}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <ArrowDownCircle size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Debemos</p>
              <p className="text-2xl font-bold text-green-600">{formatMonto(String(totalDebemos))}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <DollarSign size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Saldo Neto</p>
              <p className={`text-2xl font-bold ${totalDeben - totalDebemos >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                {formatMonto(String(totalDeben - totalDebemos))}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabla de saldos */}
      <Card padding="none">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : saldos.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No hay saldos registrados. Ejecute una liquidación diaria para comenzar.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Local</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Saldo</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-gray-700">Ultimo Mov.</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-gray-700 w-48">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {saldos.map(s => {
                  const saldo = Number(s.saldo_actual);
                  return (
                    <tr key={s.local_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Store size={16} className="text-gray-400" />
                          <span className="font-medium text-gray-900">{s.local_nombre}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`text-lg font-bold ${saldo > 0 ? 'text-red-600' : saldo < 0 ? 'text-green-600' : 'text-gray-600'}`}>
                          {formatMonto(s.saldo_actual)}
                        </span>
                        {saldo > 0 && (
                          <p className="text-xs text-red-500">Debe a banca</p>
                        )}
                        {saldo < 0 && (
                          <p className="text-xs text-green-500">Banca debe</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {s.ultimo_movimiento ? (
                          <div className="text-sm">
                            <Badge variant={TIPO_BADGE[s.ultimo_movimiento.tipo] || 'neutral'}>
                              {TIPO_LABELS[s.ultimo_movimiento.tipo] || s.ultimo_movimiento.tipo}
                            </Badge>
                            <p className="text-xs text-gray-400 mt-1">{s.ultimo_movimiento.fecha}</p>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1 justify-center">
                          <button
                            onClick={() => abrirModal(s, 'ABONO')}
                            className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-lg border border-green-200"
                          >
                            Abono
                          </button>
                          <button
                            onClick={() => abrirModal(s, 'PAGO')}
                            className="px-3 py-1.5 text-xs font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 rounded-lg border border-orange-200"
                          >
                            Pago
                          </button>
                          <button
                            onClick={() => abrirHistorial(s)}
                            className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200"
                          >
                            Historial
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modal Abono/Pago */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalTipo === 'ABONO' ? 'Registrar Abono' : 'Registrar Pago'}
      >
        <div className="space-y-4">
          {localSeleccionado && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Local:</p>
              <p className="font-semibold">{localSeleccionado.local_nombre}</p>
              <p className="text-sm text-gray-500 mt-1">Saldo actual:</p>
              <p className={`font-bold ${Number(localSeleccionado.saldo_actual) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {formatMonto(localSeleccionado.saldo_actual)}
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {modalTipo === 'ABONO' ? 'Monto del Abono' : 'Monto del Pago'}
            </label>
            <input
              type="number"
              value={monto}
              onChange={(e) => setMonto(e.target.value)}
              placeholder="0"
              min={0}
              step={1}
              className="w-full px-4 py-3 text-center text-2xl font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Referencia (opcional)</label>
            <input
              type="text"
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
              placeholder="Ej: Deposito bancario, Efectivo..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {error && (
            <div className="p-2 bg-red-50 text-red-700 text-sm rounded">{error}</div>
          )}

          <div className="flex gap-2 justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button
              variant={modalTipo === 'ABONO' ? 'success' : 'warning'}
              onClick={guardar}
              disabled={guardando || !monto || parseFloat(monto) <= 0}
            >
              {guardando ? 'Guardando...' : modalTipo === 'ABONO' ? 'Registrar Abono' : 'Registrar Pago'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Historial */}
      <Modal
        isOpen={historialOpen}
        onClose={() => setHistorialOpen(false)}
        title={`Historial - ${historialLocal?.local_nombre || ''}`}
        size="lg"
      >
        <div className="space-y-4">
          {loadingHistorial ? (
            <div className="p-8 text-center text-gray-500">Cargando...</div>
          ) : movimientos.length === 0 ? (
            <div className="p-8 text-center text-gray-500">Sin movimientos</div>
          ) : (
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="py-2 pr-2">Fecha</th>
                    <th className="py-2 pr-2">Tipo</th>
                    <th className="py-2 pr-2 text-right">Monto</th>
                    <th className="py-2 pr-2 text-right">Saldo</th>
                    <th className="py-2">Referencia</th>
                  </tr>
                </thead>
                <tbody>
                  {movimientos.map(m => (
                    <tr key={m.id} className="border-b last:border-0">
                      <td className="py-2 pr-2 text-xs">{m.fecha}</td>
                      <td className="py-2 pr-2">
                        <Badge variant={TIPO_BADGE[m.tipo] || 'neutral'}>
                          {TIPO_LABELS[m.tipo] || m.tipo}
                        </Badge>
                      </td>
                      <td className={`py-2 pr-2 text-right font-mono font-medium ${Number(m.monto) >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {Number(m.monto) >= 0 ? '+' : ''}{formatMonto(m.monto)}
                      </td>
                      <td className={`py-2 pr-2 text-right font-bold ${Number(m.saldo_resultante) > 0 ? 'text-red-600' : Number(m.saldo_resultante) < 0 ? 'text-green-600' : 'text-gray-600'}`}>
                        {formatMonto(m.saldo_resultante)}
                      </td>
                      <td className="py-2 text-xs text-gray-500">{m.referencia}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};


// ========================
// TAB 2: LIQUIDACION DIARIA
// ========================
const LiquidacionTab: React.FC = () => {
  const [fechaLiq, setFechaLiq] = useState(fechaLocalYMD);
  const [locales, setLocales] = useState<{ id: string; nombre: string }[]>([]);
  const [localSeleccionado, setLocalSeleccionado] = useState('');
  const [preview, setPreview] = useState<PreviewLiquidacion | null>(null);
  const [loading, setLoading] = useState(false);
  const [ejecutando, setEjecutando] = useState(false);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');

  // Cargar locales
  useEffect(() => {
    const cargar = async () => {
      try {
        const data = await fetchAPI<{ id: string; nombre: string }[]>('/reportes/locales');
        setLocales(data);
      } catch (e: any) {
        setError(e.message);
      }
    };
    cargar();
  }, []);

  const cargarPreview = async () => {
    if (!localSeleccionado) return;
    setLoading(true);
    setError('');
    setMensaje('');
    setPreview(null);
    try {
      const data = await fetchAPI<PreviewLiquidacion>(
        `/saldos/preview-liquidacion?local_id=${localSeleccionado}&fecha=${fechaLiq}`
      );
      setPreview(data);
    } catch (e: any) {
      setError(e.message || 'Error al calcular');
    } finally {
      setLoading(false);
    }
  };

  const ejecutarLiquidacion = async () => {
    if (!localSeleccionado) return;
    setEjecutando(true);
    setError('');
    setMensaje('');
    try {
      const result = await fetchAPI<any>('/saldos/liquidacion', {
        method: 'POST',
        body: JSON.stringify({
          local_id: localSeleccionado,
          fecha: fechaLiq,
        }),
      });
      setMensaje(`Resumen del dia: Neto ${formatMonto(result.neto_dia)}. Saldo actual: ${formatMonto(result.saldo_nuevo)}`);
      setPreview(null);
    } catch (e: any) {
      setError(e.message || 'Error al ejecutar liquidación');
    } finally {
      setEjecutando(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Liquidación Diaria</h1>
        <p className="text-gray-500">Resumen informativo: ventas - comisiones - premios del dia</p>
      </div>

      {/* Controles */}
      <Card>
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
            <Input
              type="date"
              value={fechaLiq}
              onChange={(e) => setFechaLiq(e.target.value)}
              leftIcon={<Calendar size={18} />}
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Local</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={localSeleccionado}
              onChange={(e) => setLocalSeleccionado(e.target.value)}
            >
              <option value="">Seleccione un local</option>
              {locales.map(l => (
                <option key={l.id} value={l.id}>{l.nombre}</option>
              ))}
            </select>
          </div>
          <Button
            variant="primary"
            onClick={cargarPreview}
            disabled={!localSeleccionado || loading}
          >
            {loading ? 'Calculando...' : 'Calcular'}
          </Button>
        </div>
      </Card>

      {/* Mensajes */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}
      {mensaje && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">{mensaje}</div>
      )}

      {/* Preview */}
      {preview && (
        <Card>
          <div className="space-y-4">
            {/* Header info */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg">{preview.local_nombre}</h3>
                <p className="text-sm text-gray-500">Fecha: {preview.fecha}</p>
              </div>
            </div>

            {/* Desglose */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-600 font-medium">Ventas</p>
                <p className="text-lg font-bold text-blue-700">{formatMonto(preview.total_vendido)}</p>
              </div>
              <div className="p-3 bg-yellow-50 rounded-lg">
                <p className="text-xs text-yellow-600 font-medium">Comisiones</p>
                <p className="text-lg font-bold text-yellow-700">-{formatMonto(preview.total_comision)}</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg">
                <p className="text-xs text-red-600 font-medium">Premios</p>
                <p className="text-lg font-bold text-red-700">-{formatMonto(preview.total_premios)}</p>
              </div>
              <div className={`p-3 rounded-lg ${Number(preview.neto_dia) >= 0 ? 'bg-green-50' : 'bg-orange-50'}`}>
                <p className={`text-xs font-medium ${Number(preview.neto_dia) >= 0 ? 'text-green-600' : 'text-orange-600'}`}>Neto del Dia</p>
                <p className={`text-lg font-bold ${Number(preview.neto_dia) >= 0 ? 'text-green-700' : 'text-orange-700'}`}>
                  {formatMonto(preview.neto_dia)}
                </p>
              </div>
            </div>

            {/* Saldo actual */}
            <div className="p-4 bg-gray-50 rounded-lg border-2 border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Saldo actual del local</p>
                  <p className={`text-xl font-bold ${Number(preview.saldo_actual) > 0 ? 'text-red-600' : Number(preview.saldo_actual) < 0 ? 'text-green-600' : 'text-gray-600'}`}>
                    {formatMonto(preview.saldo_actual)}
                  </p>
                </div>
              </div>
            </div>

            {/* Detalle sorteos */}
            {preview.sorteos.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Detalle por sorteo:</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b">
                        <th className="py-2 pr-2">Sorteo</th>
                        <th className="py-2 pr-2 text-right">Vendido</th>
                        <th className="py-2 pr-2 text-right">Premios</th>
                        <th className="py-2 text-right">Comision</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.sorteos.map((s, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-2 pr-2 font-medium">{s.sorteo}</td>
                          <td className="py-2 pr-2 text-right">{formatMonto(s.vendido)}</td>
                          <td className="py-2 pr-2 text-right">{formatMonto(s.premios)}</td>
                          <td className="py-2 text-right">{formatMonto(s.comision)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Boton ver resumen confirmado */}
            <div className="pt-4 border-t">
              <div className="flex justify-end">
                <Button
                  variant="primary"
                  onClick={ejecutarLiquidacion}
                  disabled={ejecutando}
                  leftIcon={<DollarSign size={18} />}
                >
                  {ejecutando ? 'Consultando...' : 'Confirmar Resumen'}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};


// ========================
// TAB 3: CIERRE CONSOLIDADO
// ========================
const CierreTab: React.FC = () => {
  const [fechaHasta, setFechaHasta] = useState(fechaLocalYMD);
  const [locales, setLocales] = useState<{ id: string; nombre: string }[]>([]);
  const [localSeleccionado, setLocalSeleccionado] = useState('');
  const [preview, setPreview] = useState<PreviewCierre | null>(null);
  const [loading, setLoading] = useState(false);
  const [ejecutando, setEjecutando] = useState(false);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');

  useEffect(() => {
    const cargar = async () => {
      try {
        const data = await fetchAPI<{ id: string; nombre: string }[]>('/reportes/locales');
        setLocales(data);
      } catch (e: any) {
        setError(e.message);
      }
    };
    cargar();
  }, []);

  const cargarPreview = async () => {
    if (!localSeleccionado) return;
    setLoading(true);
    setError('');
    setMensaje('');
    setPreview(null);
    try {
      const data = await fetchAPI<PreviewCierre>(
        `/saldos/preview-cierre?local_id=${localSeleccionado}&fecha_hasta=${fechaHasta}`
      );
      setPreview(data);
    } catch (e: any) {
      setError(e.message || 'Error al calcular');
    } finally {
      setLoading(false);
    }
  };

  const ejecutarCierre = async () => {
    if (!localSeleccionado) return;
    setEjecutando(true);
    setError('');
    setMensaje('');
    try {
      const result = await fetchAPI<any>('/saldos/cierre', {
        method: 'POST',
        body: JSON.stringify({
          local_id: localSeleccionado,
          fecha_hasta: fechaHasta,
        }),
      });
      setMensaje(
        `Cierre ejecutado. ${result.movimientos_eliminados} movimientos consolidados. ` +
        `Saldo: ${formatMonto(result.saldo_actual)} (sin cambios)`
      );
      setPreview(null);
    } catch (e: any) {
      setError(e.message || 'Error al ejecutar cierre');
    } finally {
      setEjecutando(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Cierre Consolidado</h1>
        <p className="text-gray-500">Limpieza administrativa del historial. El saldo no cambia.</p>
      </div>

      {/* Info box */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Operación técnica:</strong> Consolida todos los movimientos anteriores a la fecha seleccionada
          en un solo registro resumen. Los movimientos antiguos se eliminan pero el saldo permanece exactamente igual.
          Útil para limpiar historial acumulado.
        </p>
      </div>

      {/* Controles */}
      <Card>
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Consolidar hasta fecha</label>
            <Input
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              leftIcon={<Calendar size={18} />}
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Local</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={localSeleccionado}
              onChange={(e) => setLocalSeleccionado(e.target.value)}
            >
              <option value="">Seleccione un local</option>
              {locales.map(l => (
                <option key={l.id} value={l.id}>{l.nombre}</option>
              ))}
            </select>
          </div>
          <Button
            variant="primary"
            onClick={cargarPreview}
            disabled={!localSeleccionado || loading}
          >
            {loading ? 'Calculando...' : 'Preview'}
          </Button>
        </div>
      </Card>

      {/* Mensajes */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}
      {mensaje && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">{mensaje}</div>
      )}

      {/* Preview */}
      {preview && (
        <Card>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg">{preview.local_nombre}</h3>
                <p className="text-sm text-gray-500">Consolidar hasta: {preview.fecha_hasta}</p>
              </div>
              {preview.ya_consolidado && (
                <Badge variant="warning">Ya consolidado</Badge>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-xs text-orange-600 font-medium">Movimientos a eliminar</p>
                <p className="text-2xl font-bold text-orange-700">{preview.movimientos_a_eliminar}</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-600 font-medium">Movimientos que quedan</p>
                <p className="text-2xl font-bold text-blue-700">{preview.movimientos_posteriores}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border-2 border-gray-300">
                <p className="text-xs text-gray-600 font-medium">Saldo (no cambia)</p>
                <p className={`text-2xl font-bold ${Number(preview.saldo_actual) > 0 ? 'text-red-600' : Number(preview.saldo_actual) < 0 ? 'text-green-600' : 'text-gray-600'}`}>
                  {formatMonto(preview.saldo_actual)}
                </p>
              </div>
            </div>

            {!preview.ya_consolidado && preview.movimientos_a_eliminar > 0 && (
              <div className="flex justify-end pt-4 border-t">
                <Button
                  variant="danger"
                  onClick={ejecutarCierre}
                  disabled={ejecutando}
                  leftIcon={<Archive size={18} />}
                >
                  {ejecutando ? 'Consolidando...' : 'Ejecutar Cierre'}
                </Button>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};


// ========================
// TAB 4: HISTORIAL
// ========================
const HistorialTab: React.FC = () => {
  const [localId, setLocalId] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [locales, setLocales] = useState<{ id: string; nombre: string }[]>([]);
  const [movimientos, setMovimientos] = useState<Movimiento[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  useEffect(() => {
    const cargar = async () => {
      try {
        const data = await fetchAPI<{ id: string; nombre: string }[]>('/reportes/locales');
        setLocales(data);
      } catch (e: any) {
        setError(e.message);
      }
    };
    cargar();
  }, []);

  const cargarMovimientos = async () => {
    setLoading(true);
    setError('');
    try {
      let url = '/saldos/movimientos?limit=100';
      if (localId) url += `&local_id=${localId}`;
      if (fechaDesde) url += `&fecha_desde=${fechaDesde}`;
      if (fechaHasta) url += `&fecha_hasta=${fechaHasta}`;
      const data = await fetchAPI<Movimiento[]>(url);
      setMovimientos(data);
    } catch (e: any) {
      setError(e.message || 'Error al cargar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarMovimientos();
  }, []);

  const toggleRow = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Historial de Movimientos</h1>
        <p className="text-gray-500">Todos los movimientos de saldo</p>
      </div>

      {/* Filtros */}
      <Card>
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Local</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={localId}
              onChange={(e) => setLocalId(e.target.value)}
            >
              <option value="">Todos los locales</option>
              {locales.map(l => (
                <option key={l.id} value={l.id}>{l.nombre}</option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
            <Input
              type="date"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
            <Input
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
            />
          </div>
          <Button variant="primary" onClick={cargarMovimientos} disabled={loading}>
            {loading ? 'Cargando...' : 'Buscar'}
          </Button>
        </div>
      </Card>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}

      {/* Tabla */}
      <Card padding="none">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : movimientos.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Sin movimientos</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 w-8"></th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Fecha</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Local</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-gray-700">Tipo</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Monto</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Saldo</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Referencia</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {movimientos.map(m => {
                  const isExpanded = expandedRows.has(m.id);
                  const hasDetail = m.detalle && (m.detalle.vendido || m.detalle.movimientos_consolidados);
                  return (
                    <React.Fragment key={m.id}>
                      <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => hasDetail && toggleRow(m.id)}>
                        <td className="px-4 py-3">
                          {hasDetail && (
                            isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />
                          )}
                        </td>
                        <td className="px-4 py-3">{m.fecha}</td>
                        <td className="px-4 py-3 font-medium">{m.local_nombre}</td>
                        <td className="px-4 py-3 text-center">
                          <Badge variant={TIPO_BADGE[m.tipo] || 'neutral'}>
                            {TIPO_LABELS[m.tipo] || m.tipo}
                          </Badge>
                        </td>
                        <td className={`px-4 py-3 text-right font-mono font-medium ${Number(m.monto) >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {Number(m.monto) >= 0 ? '+' : ''}{formatMonto(m.monto)}
                        </td>
                        <td className={`px-4 py-3 text-right font-bold ${Number(m.saldo_resultante) > 0 ? 'text-red-600' : Number(m.saldo_resultante) < 0 ? 'text-green-600' : 'text-gray-600'}`}>
                          {formatMonto(m.saldo_resultante)}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">{m.referencia}</td>
                      </tr>
                      {isExpanded && m.detalle && (
                        <tr>
                          <td colSpan={7} className="px-8 py-3 bg-gray-50">
                            {/* Liquidación detail */}
                            {m.detalle.vendido && (
                              <>
                                <div className="grid grid-cols-3 gap-4 mb-2">
                                  <div>
                                    <p className="text-xs text-gray-500">Ventas</p>
                                    <p className="font-medium">{formatMonto(m.detalle.vendido)}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">Premios</p>
                                    <p className="font-medium">-{formatMonto(m.detalle.premios || '0')}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">Comisiones</p>
                                    <p className="font-medium">-{formatMonto(m.detalle.comision || '0')}</p>
                                  </div>
                                </div>
                                {m.detalle.sorteos && m.detalle.sorteos.length > 0 && (
                                  <table className="w-full text-xs mt-2">
                                    <thead>
                                      <tr className="text-gray-500 border-b">
                                        <th className="text-left py-1">Sorteo</th>
                                        <th className="text-right py-1">Vendido</th>
                                        <th className="text-right py-1">Premios</th>
                                        <th className="text-right py-1">Comision</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {m.detalle.sorteos.map((s, i) => (
                                        <tr key={i}>
                                          <td className="py-1">{s.sorteo}</td>
                                          <td className="text-right py-1">{formatMonto(s.vendido)}</td>
                                          <td className="text-right py-1">{formatMonto(s.premios)}</td>
                                          <td className="text-right py-1">{formatMonto(s.comision)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </>
                            )}
                            {/* Cierre consolidado detail */}
                            {m.detalle.movimientos_consolidados && (
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-xs text-gray-500">Movimientos consolidados</p>
                                  <p className="font-medium">{m.detalle.movimientos_consolidados}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500">Rango</p>
                                  <p className="font-medium">{m.detalle.fecha_desde} a {m.detalle.fecha_hasta}</p>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};


// ========================
// COMPONENTE PRINCIPAL CON TABS
// ========================
export const SaldosPage: React.FC = () => {
  return (
    <Tabs
      items={[
        {
          key: 'saldos',
          label: 'Saldos',
          icon: <Wallet size={16} />,
          content: <SaldosTab />,
        },
        {
          key: 'liquidacion',
          label: 'Liquidación',
          icon: <DollarSign size={16} />,
          content: <LiquidacionTab />,
        },
        {
          key: 'cierre',
          label: 'Cierre',
          icon: <Archive size={16} />,
          content: <CierreTab />,
        },
        {
          key: 'historial',
          label: 'Historial',
          icon: <FileText size={16} />,
          content: <HistorialTab />,
        },
      ]}
    />
  );
};

export default SaldosPage;
